// Cấp độ khoá học (giống các trang e-learning chuyên nghiệp).
export type CourseLevel = 'co_ban' | 'trung_binh' | 'nang_cao'

export const COURSE_LEVELS: { value: CourseLevel; label: string }[] = [
  { value: 'co_ban', label: '5+' },
  { value: 'trung_binh', label: '7+' },
  { value: 'nang_cao', label: '9+' },
]

// Phân loại khoá theo Lớp (catalog tách Lớp 10/11/12/Khác).
export type CourseGrade = '10' | '11' | '12' | 'khac'
export const GRADE_OPTIONS: { value: CourseGrade; label: string }[] = [
  { value: '10', label: 'Lớp 10' },
  { value: '11', label: 'Lớp 11' },
  { value: '12', label: 'Lớp 12' },
  { value: 'khac', label: 'Khác (ôn thi, HSG…)' },
]
export const GRADE_LABEL = (g?: string) => GRADE_OPTIONS.find((x) => x.value === g)?.label || 'Khác'
// Suy lớp từ category/title nếu khoá chưa đặt grade (tương thích khoá cũ).
export function deriveGrade(text?: string): CourseGrade {
  const t = (text || '').toLowerCase()
  if (/(^|\D)12(\D|$)|lớp\s*12/.test(t)) return '12'
  if (/(^|\D)11(\D|$)|lớp\s*11/.test(t)) return '11'
  if (/(^|\D)10(\D|$)|lớp\s*10/.test(t)) return '10'
  return 'khac'
}
export function courseGrade(c: { grade?: string; category?: string; title?: string }): CourseGrade {
  if (c.grade === '10' || c.grade === '11' || c.grade === '12' || c.grade === 'khac') return c.grade
  return deriveGrade(`${c.category || ''} ${c.title || ''}`)
}

// Phân cấp nội dung khoá: Chương (chapter) / mục Ôn tập (review) chứa các Bài.
export type CourseChapterKind = 'chapter' | 'review'
export interface CourseChapter {
  id: string
  title: string
  kind?: CourseChapterKind   // 'review' cho Ôn tập GKI/CKI/GKII/CKII
  lessonIds: string[]        // bài thuộc chương này (theo thứ tự)
}

// Preset các mục Ôn tập theo mốc kiểm tra.
export const REVIEW_PRESETS: { key: string; title: string }[] = [
  { key: 'gki', title: 'Ôn tập Giữa kỳ I' },
  { key: 'cki', title: 'Ôn tập Cuối kỳ I' },
  { key: 'gkii', title: 'Ôn tập Giữa kỳ II' },
  { key: 'ckii', title: 'Ôn tập Cuối kỳ II' },
]

// Nối các chương thành danh sách bài PHẲNG (lessonIds luôn đồng bộ với chapters).
export function flattenChapters(chapters?: CourseChapter[]): string[] {
  return (chapters || []).flatMap((c) => c.lessonIds || [])
}

// Khoá cũ chỉ có lessonIds → tạo 1 chương mặc định để hiển thị phân cấp.
export function ensureChapters(c: { chapters?: CourseChapter[]; lessonIds?: string[] }): CourseChapter[] {
  if (c.chapters && c.chapters.length) return c.chapters
  return [{ id: 'ch-1', title: 'Chương 1', kind: 'chapter', lessonIds: c.lessonIds || [] }]
}

// Khoá học online = thông tin khoá + danh sách bài giảng (theo thứ tự).
export interface CourseDoc {
  id: string
  slug: string
  ownerId?: string
  title: string
  subtitle?: string         // mô tả ngắn 1 dòng (hiển thị dưới tiêu đề)
  description?: string      // giới thiệu chi tiết
  coverImage?: string
  category?: string         // chủ đề / môn / lớp (vd: "Vật lí 11")
  grade?: CourseGrade       // phân loại Lớp 10/11/12/Khác (catalog)
  level?: CourseLevel | ''  // trình độ
  access?: 'free' | 'pro'   // quyền truy cập: miễn phí hay cần PRO
  dueDate?: string          // hạn hoàn thành (YYYY-MM-DD), rỗng = không giới hạn
  instructorName?: string       // tên giảng viên
  instructorSchool?: string     // trường / đơn vị
  instructorExperience?: string // kinh nghiệm / giới thiệu ngắn
  ratingAvg?: number            // điểm sao trung bình (server tính)
  ratingCount?: number          // số lượt đánh giá
  outcomes?: string[]       // "Bạn sẽ học được gì" (gạch đầu dòng)
  requirements?: string[]   // "Yêu cầu đầu vào"
  chapters?: CourseChapter[]  // phân cấp Chương/Ôn tập (lessonIds = nối các chương)
  lessonIds: string[]       // tham chiếu tới LessonDoc.id (theo thứ tự học) — PHẲNG, đồng bộ chapters
  // Tóm tắt bài giảng (server trả kèm khi getBySlug) — để trình xem không gọi API /lessons/{id} (chỉ GV).
  lessonSummaries?: { id: string; slug: string; title: string; cardCount: number }[]
  status: 'draft' | 'published'
  createdAt?: string
  updatedAt?: string
}

// ── Hạn hoàn thành: trạng thái "nhắc" trong ứng dụng (không cần email/cron) ──
export type DueTone = 'ok' | 'soon' | 'today' | 'over' | 'done'
export interface DueStatus { label: string; tone: DueTone; days: number }

/** Trạng thái hạn hoàn thành (dùng cho badge nhắc). null nếu không đặt hạn. */
export function dueStatus(dueISO?: string, opts?: { completed?: boolean }): DueStatus | null {
  if (!dueISO) return null
  const due = new Date(`${dueISO}T23:59:59`)
  if (isNaN(due.getTime())) return null
  const days = Math.ceil((due.getTime() - Date.now()) / 86400000)
  if (opts?.completed) return { label: 'Đã hoàn thành', tone: 'done', days }
  if (days < 0) return { label: `Quá hạn ${Math.abs(days)} ngày`, tone: 'over', days }
  if (days === 0) return { label: 'Đến hạn hôm nay', tone: 'today', days }
  if (days <= 3) return { label: `Sắp đến hạn · còn ${days} ngày`, tone: 'soon', days }
  return { label: `Còn ${days} ngày`, tone: 'ok', days }
}

/** Định dạng ngày DD/MM/YYYY cho hiển thị. */
export function fmtDue(dueISO?: string): string {
  if (!dueISO) return ''
  const d = new Date(dueISO)
  if (isNaN(d.getTime())) return dueISO
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`
}

/** Lớp màu Tailwind cho từng trạng thái hạn. */
export const DUE_TONE_CLASS: Record<DueTone, string> = {
  ok: 'bg-slate-50 text-slate-600 border-slate-200',
  soon: 'bg-amber-50 text-amber-700 border-amber-200',
  today: 'bg-orange-50 text-orange-700 border-orange-200',
  over: 'bg-rose-50 text-rose-700 border-rose-200',
  done: 'bg-emerald-50 text-emerald-700 border-emerald-200',
}
